/**
 * Attention shake - g.shake(el, opts)
 *
 * Temporarily shakes an element left-right and glows it in the color of
 * opts.type (error default) to pull the eye to that part of the page, e.g.
 * g.shake(card) on a failed login. An off-screen target is first scrolled
 * into view (smooth, centered; opts.scroll false skips it) - the shake
 * starts once the scroll lands, so the effect is never missed. Purely
 * visual: the classes and inline CSS variables set here are removed when
 * the effect ends, leaving the element untouched. Calling it again
 * mid-shake restarts the effect.
 *
 * The notify toasts drive this via their shake option (one call: the toast
 * says WHAT, the shake says WHERE) - see notify.js.
 *
 * Styles in scss/_shake.scss (.genus-shake*). Defaults live in
 * shake.defaults, overridable per call via opts.
 * Full option reference: docs/libs/genus.md.
 */

const TYPES = ['error', 'warning', 'info', 'success'];

const CSS_VARS = ['--genus-shake-duration', '--genus-shake-cycle', '--genus-shake-count'];

// element -> pending timer (scroll-delayed start or cleanup), so a re-shake
// cancels whatever the previous call still had scheduled
const active = new WeakMap();

export function shake(el, opts) {
	if (typeof el === 'string') el = document.querySelector(el);
	if (!(el instanceof HTMLElement)) return null;

	opts = { ...shake.defaults, ...opts };
	const type = TYPES.includes(opts.type) ? opts.type : 'error';
	const duration = opts.duration > 0 ? opts.duration : shake.defaults.duration;
	const shakes = opts.shakes > 0 ? Math.round(opts.shakes) : shake.defaults.shakes;

	stop(el);

	const begin = () => {
		void el.offsetWidth; // reflow so re-adding the class restarts the animation
		el.style.setProperty('--genus-shake-duration', duration + 'ms');
		el.style.setProperty('--genus-shake-cycle', (duration / shakes) + 'ms');
		el.style.setProperty('--genus-shake-count', String(shakes));
		el.classList.add('genus-shake', 'genus-shake-' + type);
		// timer, not animationend: prefers-reduced-motion may drop the animation
		active.set(el, setTimeout(() => stop(el), duration + 50));
	};

	// bring an off-screen target into view before shaking it; the smooth
	// scroll's travel time depends on the distance, so poll for arrival
	// (in view, or the position stopped moving - scroll clamped at an edge)
	// instead of guessing a delay
	if (opts.scroll !== false && needsScroll(el)) {
		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
		if (reduced) begin();
		else {
			let tries = 0;
			let lastTop = null;
			let stable = 0;
			const poll = () => {
				const top = el.getBoundingClientRect().top;
				stable = lastTop !== null && Math.abs(top - lastTop) < 1 ? stable + 1 : 0;
				lastTop = top;
				if (!needsScroll(el) || stable >= 2 || ++tries > 20) begin();
				else active.set(el, setTimeout(poll, 100));
			};
			active.set(el, setTimeout(poll, 100));
		}
	} else {
		begin();
	}
	return el;
}

// Global defaults. Override per call via opts, or app-wide by mutating this
// object (e.g. g.shake.defaults.type = 'warning').
shake.defaults = {
	type: 'error',  // error | warning | info | success (glow color)
	duration: 1000, // total effect length in ms
	shakes: 5,      // left-right cycles fitted into the duration
	scroll: true,   // scroll an off-screen target into view first
};

// Scroll only when it helps: a partially/fully off-screen target needs it,
// one that already fills the viewport does not. A few px of slack - a
// target clamped at the document edge may sit just outside the line.
function needsScroll(el) {
	const r = el.getBoundingClientRect();
	const vh = window.innerHeight;
	if (r.top <= 8 && r.bottom >= vh - 8) return false;
	return r.top < -8 || r.bottom > vh + 8;
}

function stop(el) {
	const timer = active.get(el);
	if (timer !== undefined) {
		clearTimeout(timer);
		active.delete(el);
	}
	el.classList.remove('genus-shake', ...TYPES.map((t) => 'genus-shake-' + t));
	CSS_VARS.forEach((v) => el.style.removeProperty(v));
}
